import { Button, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { capitalize } from 'lodash';

import { IconQuestionMark, IconForbidden, IconError } from '../assets/icons';
import { PictureAppDisabled, PictureMagnifyingGlassCross } from '../assets/pictures';
import { getSites } from '../redux/slices/sites';
import { useDispatch } from '../redux/store';
import { PATH_AUTH } from '../routes/paths';
import useAppName from './useAppName';

export default function useError({ code, position = null, siteName = null }) {
  const appName = useAppName();
  const dispatch = useDispatch();
  const {
    clockingApp: { forgotClockingCode }
  } = PATH_AUTH;

  const handleClickRefresh = () => {
    dispatch(
      getSites({
        clockingSiteEnabled: 1,
        position
      })
    );
  };

  let picture = {};
  let title = '';
  let severity = '';
  let subtitle = '';
  let solution = '';
  let action = {};

  switch (code) {
    case 'APP_IS_DISABLED':
      picture = <PictureAppDisabled />;
      title = `L'application n'est pas activée pour cette société`;
      subtitle = `Assurez-vous que l'application Comète On Time est activée sur votre logiciel Comète`;
      break;
    case 'LIMIT_SITE':
      picture = <IconForbidden />;
      title = "Impossible d'associer le site";
      subtitle = 'Limite de sites associables avec votre offre atteinte';
      solution = 'Contactez votre manager';
      break;
    case 'SITE_ACCESS_FORBIDDEN':
      picture = <IconForbidden />;
      title = "Vous n'avez accès à aucun autre site";
      subtitle = 'Vérifiez cette autorisation dans vos droits utilisateur Comète';
      break;
    case 'RESOURCE_ACCESS_FORBIDDEN':
      picture = <IconForbidden />;
      title = `Accès refusé`;
      subtitle = "Vous n'avez pas les permissions nécessaires pour accéder aux informations agent.";
      solution = 'Vérifiez vos droits utilisateur Comète';
      break;
    case 'ON_TIME_DISABLED_ON_SITE':
      picture = <PictureMagnifyingGlassCross />;
      title = "Comète On Time n'est pas activé sur le site";
      subtitle = 'Veuillez activer cette interface pour ce site dans Comète';
      break;
    case 'LINK_APP_ACCESS_FORBIDDEN':
      picture = <IconForbidden />;
      title = `Accès à ${appName} refusé`;
      subtitle = "Vous n'avez pas les permissions nécessaires pour accéder à cette page.";
      solution = 'Vérifiez vos droits utilisateur Comète';
      break;
    case 'ON_TIME_APP_ACCESS_FORBIDDEN':
      picture = <IconForbidden />;
      title = `Accès à ${appName} refusé`;
      subtitle = "Vous n'êtes pas autorisé à créer des pointeuses";
      solution = 'Vérifiez cette autorisation dans vos droits utilisateur Comète';
      break;
    case 'ON_TIME_NFC_UNRECOGNIZED':
      picture = <IconError fontSize={160} />;
      title = `Badge inconnu`;
      subtitle = 'Une erreur est survenue lors de du pointage. Veuillez contacter votre manager.';
      break;
    case 'ON_TIME_NFC_NOT_INITIALIZED':
      picture = <IconError fontSize={160} />;
      title = `Badge non initialiser`;
      subtitle = 'Votre badge est vierge. Veuillez contacter votre manager.';
      break;
    case 'NO_CLOCKING':
      picture = <IconQuestionMark />;
      title = "Vous n'avez pas de vacation planifiée";
      subtitle = 'Vérifiez votre planning';
      solution = 'Votre planning peut être disponible dans Comète Link';
      break;
    case 'NO_SITE':
      picture = <PictureMagnifyingGlassCross />;
      title = 'OUPS!';
      subtitle = "Aucun site n'est disponible";
      solution = 'Dans Comète, activez “Pointage sur site” depuis la fiche du site et vérifiez les sites associés.';
      action = (
        <Button variant="outlined" onClick={handleClickRefresh}>
          Actualiser
        </Button>
      );
      break;
    case 'NO_SITE_CLOCKING_CONFIGURED':
      picture = <PictureMagnifyingGlassCross />;
      title = "Ce site n'est associé à aucune pointeuse";
      subtitle = 'Veuillez associer ce ste à cette pointeuse dans Comète';
      break;
    case 'WARN_INCORRECT_CODE':
      picture = <PictureMagnifyingGlassCross />;
      title = 'Code inconnu';
      severity = 'reminder';
      subtitle = 'Vérifiez votre saisie';
      solution = 'Vérifiez votre code dans Comète Link';
      action = (
        <Link component={RouterLink} variant="subtitle2" to={forgotClockingCode}>
          Code de prise et fin de services oublié ?
        </Link>
      );
      break;
    case 'WRONG_SITE_CLOCKING':
      picture = <IconForbidden />;
      title = "Le site de votre prochaine vacation n'est pas associé à cette pointeuse";
      if (siteName) {
        title = `Pointeuse non associée au site`;
      }
      subtitle = `Votre prochaine vacation est prévue sur le site ${capitalize(
        siteName
      )} qui n'est pas associé à cette pointeuse. Contactez votre manager.`;
      break;
    case 'WARN_INCORRECT_CODE_LIST':
      picture = <IconError fontSize={160} />;
      title = `Code inconnu`;
      subtitle = `L’agent associé à ce code est introuvable sur ce site.`;
      break;
    case 'WARN_MCE_LOGIN_COMPANY_NOT_SUPPORTED':
      picture = <IconForbidden />;
      title = 'Accès agent uniquement';
      severity = 'reminder';
      subtitle = 'La connexion par code en Main Courante Électronique est réservée aux agents.';
      break;
    case 'WARN_AGENT_NO_USER_ACCOUNT':
      picture = <IconForbidden />;
      title = 'Compte agent non configuré';
      severity = 'reminder';
      subtitle = "Aucun compte utilisateur n'est associé à cet agent.";
      solution = 'Contactez votre manager';
      break;
    default:
  }

  return {
    picture,
    title,
    severity,
    subtitle,
    solution,
    action
  };
}
